import React, { useState, useEffect } from 'react';
import { Task, TaskStatus, BusinessUnit, TaskPriority, UserRole } from '../types/task';
import { getTaskService } from '../services/sharepointHelpers';
import { getAuthService } from '../services/authService';
import SearchableDropdown from './SearchableDropdown';
import {
  makeStyles,
  tokens,
  Button,
  Text,
  Title3,
  Caption1,
  Input,
  Label,
  Select,
  Textarea,
  Checkbox,
  MessageBar,
  Portal,
} from '@fluentui/react-components';
import {
  AddRegular,
  DismissRegular,
  CalendarRegular,
  TagRegular,
  SaveRegular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '16px',
  },
  modal: {
    width: '95%',
    maxWidth: '800px',
    maxHeight: '90vh',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow64,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    padding: '24px 32px 20px 32px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '32px',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  fieldGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 0',
  },
  actions: {
    padding: '20px 32px',
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
  },
  errorMessage: {
    marginBottom: '20px',
    borderRadius: tokens.borderRadiusMedium,
  },
  disabledField: {
    opacity: 0.6,
  },
  helpText: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    marginTop: '6px',
  },
});

// Display mapping for Department labels while preserving values
const displayDepartment = (unit: string) => {
  if (unit === 'Finance') return 'Finance & Development';
  if (unit === 'LEHR') return 'Legal & HR (LEHR)';
  return unit;
};

interface TaskFormProps {
  onSubmit: (taskData: Omit<Task, 'id' | 'created' | 'createdBy'>) => void;
  onCancel: () => void;
  userRole: UserRole;
  userId: string;
  userName: string;
  initialData?: Partial<Task>;
}

const TaskForm: React.FC<TaskFormProps> = ({ 
  onSubmit, 
  onCancel, 
  userRole, 
  userId, 
  userName, 
  initialData 
}) => {
  const styles = useStyles();
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    description: initialData?.description || '',
    assignedTo: initialData?.assignedTo || '',
    businessUnit: initialData?.businessUnit || BusinessUnit.SUPPORT_SERVICES,
    urgent: initialData?.urgent || false,
    status: initialData?.status || TaskStatus.OPEN,
    dueDate: initialData?.dueDate || '',
    priority: initialData?.priority || TaskPriority.MEDIUM,
    tags: initialData?.tags?.join(', ') || ''
  });

  const [users, setUsers] = useState<{ id: string; name: string; email: string; role: string; businessUnit?: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const taskService = getTaskService(getAuthService());

  // Load users for assignment
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const userList = await taskService.getUsers();
        setUsers(userList);
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };

    loadUsers();
  }, [taskService]);



  // Validate form
  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.assignedTo.trim()) {
      newErrors.assignedTo = 'Assignee is required';
    }

    if (formData.dueDate && new Date(formData.dueDate) < new Date()) {
      newErrors.dueDate = 'Due date cannot be in the past';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const taskData: Omit<Task, 'id' | 'created' | 'createdBy'> = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        assignedTo: formData.assignedTo,
        businessUnit: formData.businessUnit,
        urgent: formData.urgent,
        status: formData.status,
        dueDate: formData.dueDate || undefined,
        priority: formData.priority,
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : []
      };

      await onSubmit(taskData);
    } catch (error) {
      console.error('Error submitting task:', error);
      setErrors({ submit: 'Failed to create task. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Handle input changes
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Portal>
      <div className={styles.overlay} onClick={onCancel}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.headerContent}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ 
                  width: '32px', 
                  height: '32px', 
                  borderRadius: '50%', 
                  backgroundColor: tokens.colorPaletteBlueBackground2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <AddRegular style={{ color: tokens.colorPaletteBlueForeground2 }} />
                </div>
                <div>
                  <Title3>Create New Task</Title3>
                  <Caption1>Fill in the details below to create a new task</Caption1>
                </div>
              </div>
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                onClick={onCancel}
              />
            </div>
          </div>

          {/* Content */}
          <div className={styles.content}>
            {/* Error Display */}
            {errors.submit && (
              <MessageBar className={styles.errorMessage}>
                {errors.submit}
              </MessageBar>
            )}

            <form onSubmit={handleSubmit} className={styles.form}>
              {/* Title */}
              <div className={styles.field}>
                <Label htmlFor="title" required>
                  Task Title
                </Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e, data) => handleInputChange('title', data.value)}
                  placeholder="Enter task title"
                  appearance={errors.title ? 'outline' : 'outline'}
                  contentAfter={errors.title && <Text size={100} style={{ color: tokens.colorPaletteRedForeground2 }}>⚠</Text>}
                />
                {errors.title && (
                  <Text size={100} style={{ color: tokens.colorPaletteRedForeground2 }}>
                    {errors.title}
                  </Text>
                )}
              </div>

              {/* Description */}
              <div className={styles.field}>
                <Label htmlFor="description" required>
                  Description
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e, data) => handleInputChange('description', data.value)}
                  placeholder="Enter task description"
                  rows={4}
                  appearance={errors.description ? 'outline' : 'outline'}
                />
                {errors.description && (
                  <Text size={100} style={{ color: tokens.colorPaletteRedForeground2 }}>
                    {errors.description}
                  </Text>
                )}
              </div>

                                            {/* Department - Show first for executives, hidden for managers */}
               {userRole === UserRole.EXECUTIVE ? (
                 <div className={styles.field}>
                   <Label htmlFor="businessUnit">
                     Department
                   </Label>
                   <Select
                     id="businessUnit"
                     value={formData.businessUnit}
                     onChange={(e, data) => handleInputChange('businessUnit', data.value)}
                   >
                     {Object.values(BusinessUnit).map(unit => (
                       <option key={unit} value={unit}>{displayDepartment(unit)}</option>
                     ))}
                   </Select>
                   <Text size={100} className={styles.helpText}>
                     Select the department this task belongs to
                   </Text>
                 </div>
               ) : (
                 // Hidden department field for managers (they can only assign to themselves)
                 <input 
                   type="hidden" 
                   value={formData.businessUnit} 
                   onChange={(e) => handleInputChange('businessUnit', e.target.value)}
                 />
               )}

               {/* Assigned To */}
                 <SearchableDropdown
                   label="Assigned To"
                   placeholder="Type to search for a volunteer, manager, or business leader..."
                   options={users}
                   value={formData.assignedTo}
                   onChange={(value) => handleInputChange('assignedTo', value)}
                   required
                   error={errors.assignedTo}
                   helpText="Search by name, role, or business unit to find the person to assign this task to"
                 />

              {/* Priority and Urgent */}
              <div className={styles.fieldGrid}>
                <div className={styles.field}>
                  <Label htmlFor="priority">
                    Priority
                  </Label>
                  <Select
                    id="priority"
                    value={formData.priority}
                    onChange={(e, data) => handleInputChange('priority', data.value)}
                  >
                    {Object.values(TaskPriority).map(priority => (
                      <option key={priority} value={priority}>{priority}</option>
                    ))}
                  </Select>
                </div>

                <div className={styles.field}>
                  <Label>Urgent</Label>
                  <div className={styles.checkboxContainer}>
                    <Checkbox
                      id="urgent"
                      checked={formData.urgent}
                      onChange={(e, data) => handleInputChange('urgent', data.checked)}
                    />
                    <Label htmlFor="urgent" weight="regular">
                      Mark as urgent
                    </Label>
                  </div>
                </div>
              </div>

              {/* Status and Due Date */}
              <div className={styles.fieldGrid}>
                <div className={styles.field}>
                  <Label htmlFor="status">
                    Status
                  </Label>
                  <Select
                    id="status"
                    value={formData.status}
                    onChange={(e, data) => handleInputChange('status', data.value)}
                  >
                    {Object.values(TaskStatus).map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </Select>
                </div>

                <div className={styles.field}>
                  <Label htmlFor="dueDate">
                    Due Date
                  </Label>
                  <Input
                    id="dueDate"
                    type="date"
                    value={formData.dueDate}
                    onChange={(e, data) => handleInputChange('dueDate', data.value)}
                    contentBefore={<CalendarRegular />}
                    appearance={errors.dueDate ? 'outline' : 'outline'}
                  />
                  {errors.dueDate && (
                    <Text size={100} style={{ color: tokens.colorPaletteRedForeground2 }}>
                      {errors.dueDate}
                    </Text>
                  )}
                </div>
              </div>

              {/* Tags */}
              <div className={styles.field}>
                <Label htmlFor="tags">
                  Tags
                </Label>
                <Input
                  id="tags"
                  value={formData.tags}
                  onChange={(e, data) => handleInputChange('tags', data.value)}
                  contentBefore={<TagRegular />}
                  placeholder="Enter tags separated by commas"
                />
                <Text size={100} className={styles.helpText}>
                  Separate tags with commas
                </Text>
              </div>
            </form>
          </div>

          {/* Footer Actions */}
          <div className={styles.actions}>
            <Button appearance="subtle" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              icon={<SaveRegular />}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Task'}
            </Button>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default TaskForm; 